// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import getTextAlignmentField from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import getVerticalAlignmentField, {
    verticalAlignmentOptions,
} from "@store/Framework/Common/Fields/Toolkit/verticalAlignment";
import HeadingComponent, { HeadingProps, HeadingStyles } from "@store/Library/Components/Toolkit/Heading";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    usePageTitle = "usePageTitle",
    title = "title",
    variant = "variant",
    forwardAs = "forwardAs",
    textAlign = "textAlign",
    verticalAlign = "verticalAlign",
    showActionZone = "showActionZone",
    colorOverride = "colorOverride",
    customCSS = "customCSS",
}

const textAlignField = getTextAlignmentField(fields.textAlign);
const verticalAlignField = getVerticalAlignmentField(fields.verticalAlign);

interface Props extends WidgetProps {
    fields: {
        [fields.usePageTitle]: HeadingProps["usePageTitle"];
        [fields.title]: HeadingProps["title"];
        [fields.variant]: HeadingProps["variant"];
        [fields.forwardAs]: HeadingProps["forwardAs"];
        [fields.textAlign]: HeadingProps["textAlign"];
        [fields.verticalAlign]: HeadingProps["verticalAlign"];
        [fields.showActionZone]: boolean;
        [fields.colorOverride]: string;
        [fields.customCSS]: string;
    };
}

const Heading: FC<Props> = ({
    id,
    fields: {
        usePageTitle,
        title,
        variant,
        forwardAs,
        textAlign,
        verticalAlign,
        showActionZone,
        colorOverride,
        customCSS,
    },
}) => {
    const zoneIsActive = useZoneIsActive(id, "Content") && showActionZone;

    const styles: HeadingStyles = useMemo(() => {
        return {
            title: {
                css: css`
                    ${({ theme }) => cssProp("color", colorOverride, x => resolveColor(x, theme))}
                    ${customCSS}
                `,
            },
        };
    }, [colorOverride, customCSS]);

    return (
        <HeadingComponent
            extendedStyles={styles}
            usePageTitle={usePageTitle}
            title={title}
            variant={variant}
            forwardAs={forwardAs}
            textAlign={textAlignField.parse(textAlign)}
            verticalAlign={verticalAlignField.parse(verticalAlign)}
        >
            {zoneIsActive && <Zone contentId={id} zoneName="Content" />}
        </HeadingComponent>
    );
};

const defaultCustomCss = `& {
}`;

const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Heading,
    definition: {
        group: "Basic",
        icon: "heading",
        fieldDefinitions: [
            {
                name: fields.showActionZone,
                displayName: "Show Action Zone",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.usePageTitle,
                displayName: "Use Page Title",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
            },
            {
                name: fields.title,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: x => !x.fields.usePageTitle,
            },
            {
                name: fields.variant,
                displayName: "Format",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "" },
                    { displayName: "Paragraph", value: "p" },
                    { displayName: "Heading 1", value: "h1" },
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                    { displayName: "Banner", value: "bannerTitle" },
                    { displayName: "Banner Secondary", value: "bannerSecondaryTitle" },
                ],
                defaultValue: "h1",
                hideEmptyOption: true,
            },
            {
                name: fields.forwardAs,
                displayName: "HTML Tag",
                fieldType: "General",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Match Format", value: "" },
                    { displayName: "span", value: "span" },
                    { displayName: "p", value: "p" },
                    { displayName: "h1", value: "h1" },
                    { displayName: "h2", value: "h2" },
                    { displayName: "h3", value: "h3" },
                    { displayName: "h4", value: "h4" },
                    { displayName: "h5", value: "h5" },
                    { displayName: "h6", value: "h6" },
                ],
                defaultValue: "",
                hideEmptyOption: true,
                tooltip: "Set a HTML tag that differs from the visual format for SEO purposes",
            },
            {
                ...textAlignField.definition,
                displayName: "Alignment",
            },
            {
                ...verticalAlignField.definition,
                defaultValue: verticalAlignmentOptions.bottom.id,
                isVisible: x => x.fields.showActionZone,
            },
            {
                name: fields.colorOverride,
                displayName: "Color Override",
                editorTemplate: "CustomColorPickerField",
                fieldType: "General",
                defaultValue: "",
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
